import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Calendar,
  ExternalLink,
  Home,
  Layers,
  MapPin,
} from 'lucide-react';

const backDef: SlotGroupDefinition = {
  label: 'Back Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/investors' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Back to Investors' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const heroDef: SlotGroupDefinition = {
  label: 'Hero Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/property/5753550520975360/019d02f5-7ce9-72af-ac2a-eb4a690dfed8.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'The Jaxon' },
  },
};

const websiteCtaDef: SlotGroupDefinition = {
  label: 'Property Website CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.thejaxonhouston.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Property Website' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const gallery1Def: SlotGroupDefinition = {
  label: 'Gallery Image 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/property/5753550520975360/019d02f5-7ce9-72af-ac2a-eb4a690dfed8.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'The Jaxon exterior' },
  },
};

const gallery2Def: SlotGroupDefinition = {
  label: 'Gallery Image 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community amenities' },
  },
};

const gallery3Def: SlotGroupDefinition = {
  label: 'Gallery Image 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Resort-style pool' },
  },
};

const gallery4Def: SlotGroupDefinition = {
  label: 'Gallery Image 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Apartment interior' },
  },
};

const gallery5Def: SlotGroupDefinition = {
  label: 'Gallery Image 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Apartment bedroom' },
  },
};

const gallery6Def: SlotGroupDefinition = {
  label: 'Gallery Image 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Fitness center' },
  },
};

const gallery7Def: SlotGroupDefinition = {
  label: 'Gallery Image 7',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community amenity' },
  },
};

const gallery8Def: SlotGroupDefinition = {
  label: 'Gallery Image 8',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community exterior' },
  },
};

interface GalleryImage {
  image: string;
  alt?: string;
}

function withGalleryDefault(def: SlotGroupDefinition, item?: GalleryImage): SlotGroupDefinition {
  if (!item?.image) return def;
  return {
    label: def.label,
    fields: {
      image: { ...def.fields.image, defaultValue: item.image },
      alt: { ...def.fields.alt, defaultValue: item.alt || def.fields.alt.defaultValue },
    },
  };
}

function SectionEyebrow({ children }: { children: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-secondary))]" />
      <span className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60">
        {children}
      </span>
    </div>
  );
}

interface FeaturedProjectDetailProps {
  tokenPrefix?: string;
  title?: string;
  location?: string;
  status?: string;
  propertyType?: string;
  units?: string;
  buildType?: string;
  completionDate?: string;
  overview?: string;
  propertyInfo?: string;
  caseStudy?: string;
  caseStudyHeadline?: string;
  heroImage?: string;
  heroAlt?: string;
  websiteUrl?: string;
  gallery?: GalleryImage[];
}

export function FeaturedProjectDetail({
  tokenPrefix,
  title: titleProp,
  location: locationProp,
  status: statusProp,
  propertyType: propertyTypeProp,
  units: unitsProp,
  buildType: buildTypeProp,
  completionDate: completionDateProp,
  overview: overviewProp,
  propertyInfo: propertyInfoProp,
  caseStudy: caseStudyProp,
  caseStudyHeadline: caseStudyHeadlineProp,
  heroImage: heroImageProp,
  heroAlt: heroAltProp,
  websiteUrl: websiteUrlProp,
  gallery: galleryProp,
}: FeaturedProjectDetailProps) {
  const prefix = tokenPrefix || 'featuredprojectdetail';
  const titlePath = `${prefix}.title`;
  const locationPath = `${prefix}.location`;
  const statusPath = `${prefix}.status`;
  const propertyTypePath = `${prefix}.property_type`;
  const unitsPath = `${prefix}.units`;
  const buildTypePath = `${prefix}.build_type`;
  const completionDatePath = `${prefix}.completion_date`;
  const overviewPath = `${prefix}.overview`;
  const propertyInfoPath = `${prefix}.property_info`;
  const caseStudyPath = `${prefix}.case_study`;
  const caseStudyHeadlinePath = `${prefix}.case_study_headline`;
  const backPath = `${prefix}.back`;
  const heroPath = `${prefix}.hero`;
  const websitePath = `${prefix}.website_cta`;
  const gallery1Path = `${prefix}.gallery_1`;
  const gallery2Path = `${prefix}.gallery_2`;
  const gallery3Path = `${prefix}.gallery_3`;
  const gallery4Path = `${prefix}.gallery_4`;
  const gallery5Path = `${prefix}.gallery_5`;
  const gallery6Path = `${prefix}.gallery_6`;
  const gallery7Path = `${prefix}.gallery_7`;
  const gallery8Path = `${prefix}.gallery_8`;

  const title = useContentToken(titlePath, { defaultValue: titleProp || 'The Jaxon' });
  const location = useContentToken(locationPath, {
    defaultValue: locationProp || 'Houston, TX',
  });
  const status = useContentToken(statusPath, { defaultValue: statusProp || 'Featured' });
  const propertyType = useContentToken(propertyTypePath, {
    defaultValue: propertyTypeProp || 'Multifamily',
  });
  const units = useContentToken(unitsPath, { defaultValue: unitsProp || '312' });
  const buildType = useContentToken(buildTypePath, {
    defaultValue: buildTypeProp || 'Value-Add',
  });
  const completionDate = useContentToken(completionDatePath, {
    defaultValue: completionDateProp || 'May 31, 2021',
  });
  const caseStudyHeadline = useContentToken(caseStudyHeadlinePath, {
    defaultValue: caseStudyHeadlineProp || 'Investment Thesis & Execution',
  });

  const runtimeHeroDef = useMemo<SlotGroupDefinition>(
    () => ({
      label: heroDef.label,
      fields: {
        image: {
          ...heroDef.fields.image,
          defaultValue: heroImageProp || heroDef.fields.image.defaultValue,
        },
        alt: {
          ...heroDef.fields.alt,
          defaultValue: heroAltProp || titleProp || heroDef.fields.alt.defaultValue,
        },
      },
    }),
    [heroAltProp, heroImageProp, titleProp],
  );

  const runtimeWebsiteDef = useMemo<SlotGroupDefinition>(
    () => ({
      label: websiteCtaDef.label,
      fields: {
        url: {
          ...websiteCtaDef.fields.url,
          defaultValue: websiteUrlProp || websiteCtaDef.fields.url.defaultValue,
        },
        label: websiteCtaDef.fields.label,
        _target: websiteCtaDef.fields._target,
      },
    }),
    [websiteUrlProp],
  );

  const back = useSlotGroup(backPath, backDef);
  const hero = useSlotGroup(heroPath, runtimeHeroDef);
  const websiteCta = useSlotGroup(websitePath, runtimeWebsiteDef);
  const runtimeGalleryDefs = useMemo(() => {
    const items: (GalleryImage | undefined)[] = galleryProp?.length
      ? galleryProp
      : heroImageProp
        ? [{ image: heroImageProp, alt: heroAltProp || titleProp }]
        : [];
    return [
      gallery1Def,
      gallery2Def,
      gallery3Def,
      gallery4Def,
      gallery5Def,
      gallery6Def,
      gallery7Def,
      gallery8Def,
    ].map((def, index) => withGalleryDefault(def, items[index]));
  }, [galleryProp, heroAltProp, heroImageProp, titleProp]);

  const gallery1 = useSlotGroup(gallery1Path, runtimeGalleryDefs[0]);
  const gallery2 = useSlotGroup(gallery2Path, runtimeGalleryDefs[1]);
  const gallery3 = useSlotGroup(gallery3Path, runtimeGalleryDefs[2]);
  const gallery4 = useSlotGroup(gallery4Path, runtimeGalleryDefs[3]);
  const gallery5 = useSlotGroup(gallery5Path, runtimeGalleryDefs[4]);
  const gallery6 = useSlotGroup(gallery6Path, runtimeGalleryDefs[5]);
  const gallery7 = useSlotGroup(gallery7Path, runtimeGalleryDefs[6]);
  const gallery8 = useSlotGroup(gallery8Path, runtimeGalleryDefs[7]);

  const galleryImages = [
    { values: gallery1, path: gallery1Path },
    { values: gallery2, path: gallery2Path },
    { values: gallery3, path: gallery3Path },
    { values: gallery4, path: gallery4Path },
    { values: gallery5, path: gallery5Path },
    { values: gallery6, path: gallery6Path },
    { values: gallery7, path: gallery7Path },
    { values: gallery8, path: gallery8Path },
  ]
    .map((item, index) => ({
      url: item.values.image || '',
      alt: item.values.alt || '',
      path: item.path,
      def: runtimeGalleryDefs[index],
    }))
    .filter((item) => item.url);

  const [galleryIndex, setGalleryIndex] = useState(0);
  const slideCount = galleryImages.length;
  const activeSlide = Math.min(galleryIndex, Math.max(slideCount - 1, 0));
  const goToSlide = (index: number) => setGalleryIndex((index + slideCount) % slideCount);

  const infoCards = [
    {
      icon: Building2,
      label: 'Property Type',
      value: propertyType || propertyTypeProp || 'Multifamily',
      path: propertyTypePath,
      propDefault: propertyTypeProp || 'Multifamily',
    },
    {
      icon: Home,
      label: 'Number of Units',
      value: units || unitsProp || '312',
      path: unitsPath,
      propDefault: unitsProp || '312',
    },
    {
      icon: Layers,
      label: 'Build Type',
      value: buildType || buildTypeProp || 'Value-Add',
      path: buildTypePath,
      propDefault: buildTypeProp || 'Value-Add',
    },
    {
      icon: Calendar,
      label: 'Completion Date',
      value: completionDate || completionDateProp || 'May 31, 2021',
      path: completionDatePath,
      propDefault: completionDateProp || 'May 31, 2021',
    },
  ];

  const heroUrl =
    hero.image ||
    heroImageProp ||
    'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/property/5753550520975360/019d02f5-7ce9-72af-ac2a-eb4a690dfed8.webp';
  const heroAlt = hero.alt || heroAltProp || title || titleProp || 'Featured project';
  const displayTitle = title || titleProp || 'The Jaxon';
  const displayLocation = location || locationProp || 'Houston, TX';
  const displayStatus = status || statusProp || 'Featured';
  const websiteUrl = websiteCta.url || websiteUrlProp || '';

  return (
    <div className="w-full overflow-x-clip bg-[#F0F4F8]">
      <section className="relative h-[460px] w-full overflow-clip md:h-[560px]">
        <EditableSlotGroup groupPath={heroPath} definition={runtimeHeroDef}>
          <div className="absolute inset-0">
            <VibesitesImage
              url={heroUrl}
              alt={heroAlt}
              priority
              sizes="100vw"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgb(var(--ala-dark)/0.4)_0%,_rgb(var(--ala-dark)/0.9)_100%)]" />
          </div>
        </EditableSlotGroup>

        <div className="absolute inset-0 z-10 flex flex-col justify-end p-6 pb-10 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <EditableSlotGroup groupPath={backPath} definition={backDef}>
              <a
                href={back.url || '/investors'}
                target={back._target || '_self'}
                className="mb-4 inline-flex items-center gap-2 font-heading text-sm font-medium text-white/80 transition-colors hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={2} aria-hidden />
                {back.label || 'Back to Investors'}
              </a>
            </EditableSlotGroup>

            <div className="mb-3 flex items-center gap-2">
              <EditableToken path={statusPath} as="span" label="Status Badge" defaultValue={statusProp || 'Featured'}>
                <span className="rounded-full bg-[rgb(var(--ala-secondary))] px-3 py-1 font-heading text-sm font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-secondary))]">
                  {displayStatus}
                </span>
              </EditableToken>
            </div>

            <EditableToken path={titlePath} as="h1" label="Project Title" defaultValue={titleProp || 'The Jaxon'}>
              <span className="font-heading text-[clamp(36px,5vw,72px)] font-semibold leading-[1.05] tracking-[-0.02em] text-white">
                {displayTitle}
              </span>
            </EditableToken>

            <div className="mt-3 flex items-center gap-2 text-white/80">
              <MapPin className="h-5 w-5 shrink-0" aria-hidden />
              <EditableToken
                path={locationPath}
                as="span"
                label="Location"
                defaultValue={locationProp || 'Houston, TX'}
              >
                <span className="font-heading text-lg">{displayLocation}</span>
              </EditableToken>
            </div>

            <EditableSlotGroup groupPath={websitePath} definition={runtimeWebsiteDef}>
              {websiteUrl ? (
                <a
                  href={websiteUrl}
                  target={websiteCta._target || '_blank'}
                  rel="noopener noreferrer"
                  className="group mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-4 font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#4080d0]"
                >
                  {websiteCta.label || 'View Property Website'}
                  <ExternalLink className="h-4 w-4" strokeWidth={2} aria-hidden />
                </a>
              ) : (
                <span />
              )}
            </EditableSlotGroup>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-16 px-6 py-16 md:px-12">
        <section className="flex flex-col gap-6">
          <SectionEyebrow>Property Information</SectionEyebrow>
          <div className="grid grid-cols-2 gap-px overflow-clip rounded-[12px] border border-[#d5d5d5] bg-[#d5d5d5] md:grid-cols-4">
            {infoCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="flex flex-col gap-3 bg-white p-6 md:p-8">
                  <div className="flex items-center gap-2 text-[rgb(var(--ala-secondary))]">
                    <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                    <span className="font-heading text-sm font-medium uppercase tracking-wide text-[rgb(var(--ala-dark))]/60">
                      {card.label}
                    </span>
                  </div>
                  <EditableToken path={card.path} as="span" label={card.label} defaultValue={card.propDefault}>
                    <span className="font-heading text-[clamp(18px,1.6vw,24px)] font-semibold leading-[1.2] tracking-[-0.02em] text-[rgb(var(--ala-dark))]">
                      {card.value}
                    </span>
                  </EditableToken>
                </div>
              );
            })}
          </div>
        </section>

        <section className="flex flex-col gap-6">
          <SectionEyebrow>Property Overview</SectionEyebrow>
          <RichTextContent
            path={overviewPath}
            label="Overview"
            as="div"
            defaultValue={
              overviewProp ||
              '<p>The Jaxon is a premier multifamily community near Lake Houston in Kingwood, offering modern living with premium amenities and a prime location near major employment centers and entertainment districts.</p>'
            }
            className="font-heading text-[clamp(16px,1.3vw,19px)] font-normal leading-[1.7] text-[#333333] [&_p]:mb-4 [&_p:last-child]:mb-0"
          />
          <RichTextContent
            path={propertyInfoPath}
            label="Property Details"
            as="div"
            defaultValue={
              propertyInfoProp ||
              '<p>The Jaxon features 312 apartment homes with a mix of one-, two-, and three-bedroom floor plans. Community amenities include a resort-style pool, fitness center, co-working lounge, and pet spa, all positioned within easy reach of everyday convenience and Houston\'s growth corridors.</p>'
            }
            className="font-heading text-[clamp(16px,1.3vw,19px)] font-normal leading-[1.7] text-[#333333] [&_p]:mb-4 [&_p:last-child]:mb-0"
          />
        </section>

        <section className="flex flex-col gap-6 rounded-[16px] border border-[#e0e6ec] bg-white p-8 md:p-12">
          <SectionEyebrow>Case Study</SectionEyebrow>
          <EditableToken
            path={caseStudyHeadlinePath}
            as="h2"
            label="Case Study Headline"
            defaultValue={caseStudyHeadlineProp || 'Investment Thesis & Execution'}
          >
            <span className="font-heading text-[clamp(28px,3vw,44px)] font-semibold leading-[1.1] tracking-[-0.02em] text-[rgb(var(--ala-dark))]">
              {caseStudyHeadline || caseStudyHeadlineProp || 'Investment Thesis & Execution'}
            </span>
          </EditableToken>
          <RichTextContent
            path={caseStudyPath}
            label="Case Study"
            as="div"
            defaultValue={
              caseStudyProp ||
              '<p>Acquired in mid-2021 with a value-add thesis centered on interior renovations, amenity upgrades, and professionalized on-site management. Within 18 months, the team completed unit renovations across 70% of the community, launched a new resident experience program, and implemented dynamic revenue management. Rental rates increased 22% while occupancy held above 96%, driving meaningful NOI growth and a revaluation well above the purchase price.</p>'
            }
            className="font-heading text-[clamp(16px,1.3vw,19px)] font-normal leading-[1.7] text-[#333333] [&_p]:mb-4 [&_p:last-child]:mb-0"
          />
        </section>

        {slideCount > 0 ? (
          <section className="flex flex-col gap-6" aria-roledescription="carousel" aria-label={`${displayTitle} gallery`}>
            <SectionEyebrow>Gallery</SectionEyebrow>
            <div className="relative h-[320px] w-full overflow-clip rounded-[12px] bg-[rgb(var(--ala-dark))] md:h-[520px]">
              <motion.div
                className={`flex h-full w-full ${slideCount > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}
                animate={{ x: `-${activeSlide * 100}%` }}
                transition={{ type: 'spring', stiffness: 260, damping: 32 }}
                drag={slideCount > 1 ? 'x' : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.25}
                onDragEnd={(_, info) => {
                  if (info.offset.x < -60 || info.velocity.x < -500) goToSlide(activeSlide + 1);
                  else if (info.offset.x > 60 || info.velocity.x > 500) goToSlide(activeSlide - 1);
                }}
              >
                {galleryImages.map((item, index) => (
                  <div
                    key={item.path}
                    className="relative h-full w-full shrink-0"
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${index + 1} of ${slideCount}`}
                    aria-hidden={index !== activeSlide}
                  >
                    <EditableSlotGroup groupPath={item.path} definition={item.def}>
                      <div className="absolute inset-0">
                        <VibesitesImage
                          url={item.url}
                          alt={item.alt || `${displayTitle} gallery ${index + 1}`}
                          sizes="(min-width: 1200px) 1200px, 100vw"
                          className="pointer-events-none h-full w-full select-none object-cover"
                        />
                      </div>
                    </EditableSlotGroup>
                  </div>
                ))}
              </motion.div>

              {slideCount > 1 ? (
                <>
                  <button
                    type="button"
                    aria-label="Previous image"
                    onClick={() => goToSlide(activeSlide - 1)}
                    className="absolute left-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/30"
                  >
                    <ArrowLeft className="h-[18px] w-[18px]" strokeWidth={2.5} aria-hidden />
                  </button>
                  <button
                    type="button"
                    aria-label="Next image"
                    onClick={() => goToSlide(activeSlide + 1)}
                    className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/30"
                  >
                    <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.5} aria-hidden />
                  </button>
                  <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
                    {galleryImages.map((item, index) => (
                      <button
                        key={item.path}
                        type="button"
                        aria-label={`Go to image ${index + 1}`}
                        aria-current={index === activeSlide}
                        onClick={() => goToSlide(index)}
                        className={`h-2.5 rounded-full transition-all ${
                          index === activeSlide ? 'w-7 bg-white' : 'w-2.5 bg-white/50 hover:bg-white/80'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="absolute bottom-4 right-4 z-10 rounded-full bg-[rgb(var(--ala-dark))]/70 px-3 py-1 font-heading text-sm font-medium text-white backdrop-blur-md">
                    {activeSlide + 1} / {slideCount}
                  </div>
                </>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>

      {/* Schema-lint anchors */}
      <div className="hidden" style={{ display: 'none' }}>
        <EditableToken path="featuredprojectdetail.title" as="span">
          {''}
        </EditableToken>
        <EditableToken path="featuredprojectdetail.location" as="span">
          {''}
        </EditableToken>
        <EditableToken path="featuredprojectdetail.status" as="span">
          {''}
        </EditableToken>
        <EditableToken path="featuredprojectdetail.property_type" as="span">
          {''}
        </EditableToken>
        <EditableToken path="featuredprojectdetail.units" as="span">
          {''}
        </EditableToken>
        <EditableToken path="featuredprojectdetail.build_type" as="span">
          {''}
        </EditableToken>
        <EditableToken path="featuredprojectdetail.completion_date" as="span">
          {''}
        </EditableToken>
        <EditableToken path="featuredprojectdetail.case_study_headline" as="span">
          {''}
        </EditableToken>
        <RichTextContent path="featuredprojectdetail.overview" as="div" />
        <RichTextContent path="featuredprojectdetail.property_info" as="div" />
        <RichTextContent path="featuredprojectdetail.case_study" as="div" />
        <EditableSlotGroup groupPath="featuredprojectdetail.back" definition={backDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.hero" definition={heroDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.website_cta" definition={websiteCtaDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.gallery_1" definition={gallery1Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.gallery_2" definition={gallery2Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.gallery_3" definition={gallery3Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.gallery_4" definition={gallery4Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.gallery_5" definition={gallery5Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.gallery_6" definition={gallery6Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.gallery_7" definition={gallery7Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredprojectdetail.gallery_8" definition={gallery8Def}>
          <span />
        </EditableSlotGroup>
      </div>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeaturedProjectDetail',
  displayName: 'Featured Project Detail',
  tokens: {
    'featuredprojectdetail.title': {
      label: 'Project Title',
      type: 'text',
      defaultValue: 'The Jaxon',
    },
    'featuredprojectdetail.location': {
      label: 'Location',
      type: 'text',
      defaultValue: 'Houston, TX',
    },
    'featuredprojectdetail.status': {
      label: 'Status Badge',
      type: 'text',
      defaultValue: 'Featured',
    },
    'featuredprojectdetail.property_type': {
      label: 'Property Type',
      type: 'text',
      defaultValue: 'Multifamily',
    },
    'featuredprojectdetail.units': {
      label: 'Number of Units',
      type: 'text',
      defaultValue: '312',
    },
    'featuredprojectdetail.build_type': {
      label: 'Build Type',
      type: 'text',
      defaultValue: 'Value-Add',
    },
    'featuredprojectdetail.completion_date': {
      label: 'Completion Date',
      type: 'text',
      defaultValue: 'May 31, 2021',
    },
    'featuredprojectdetail.overview': {
      label: 'Overview',
      type: 'richtext',
      defaultValue:
        '<p>The Jaxon is a premier multifamily community near Lake Houston in Kingwood, offering modern living with premium amenities and a prime location near major employment centers and entertainment districts.</p>',
    },
    'featuredprojectdetail.property_info': {
      label: 'Property Details',
      type: 'richtext',
      defaultValue:
        "<p>The Jaxon features 312 apartment homes with a mix of one-, two-, and three-bedroom floor plans. Community amenities include a resort-style pool, fitness center, co-working lounge, and pet spa, all positioned within easy reach of everyday convenience and Houston's growth corridors.</p>",
    },
    'featuredprojectdetail.case_study_headline': {
      label: 'Case Study Headline',
      type: 'text',
      defaultValue: 'Investment Thesis & Execution',
    },
    'featuredprojectdetail.case_study': {
      label: 'Case Study',
      type: 'richtext',
      defaultValue:
        '<p>Acquired in mid-2021 with a value-add thesis centered on interior renovations, amenity upgrades, and professionalized on-site management. Within 18 months, the team completed unit renovations across 70% of the community, launched a new resident experience program, and implemented dynamic revenue management. Rental rates increased 22% while occupancy held above 96%, driving meaningful NOI growth and a revaluation well above the purchase price.</p>',
    },
  },
  slotGroups: {
    'featuredprojectdetail.back': backDef,
    'featuredprojectdetail.hero': heroDef,
    'featuredprojectdetail.website_cta': websiteCtaDef,
    'featuredprojectdetail.gallery_1': gallery1Def,
    'featuredprojectdetail.gallery_2': gallery2Def,
    'featuredprojectdetail.gallery_3': gallery3Def,
    'featuredprojectdetail.gallery_4': gallery4Def,
    'featuredprojectdetail.gallery_5': gallery5Def,
    'featuredprojectdetail.gallery_6': gallery6Def,
    'featuredprojectdetail.gallery_7': gallery7Def,
    'featuredprojectdetail.gallery_8': gallery8Def,
  },
};

(FeaturedProjectDetail as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FeaturedProjectDetail;
